import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { BarChart3, Edit, Eye, EyeOff, Image, Plus, Save, Shield, Trash2, Users, Video, X } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const emptyForm = {
  name: '',
  slug: '',
  title: '',
  archetype: '',
  certifications: '',
  short_bio: '',
  detailed_bio: '',
  bio: '',
  practices: '',
  specializes_in: '',
  gender_focus: '',
  service_categories: '',
  photo_url: '',
  consultation_url: '',
  booking_url: '',
  published: true,
};

const emptyVideoReviewForm = {
  reviewer_name: '',
  video_url: '',
  caption: '',
  poster_url: '',
  sort_order: 0,
  published: true,
};

const emptyWrittenReviewForm = {
  reviewer_name: '',
  rating: 5,
  comment: '',
  review_date: '',
};

const splitValues = (value) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const joinValues = (value) => (Array.isArray(value) ? value.join(', ') : value || '');

const toForm = (practitioner) => ({
  name: practitioner.Name || '',
  slug: practitioner.slug || '',
  title: practitioner.Title || '',
  archetype: practitioner.Archetype || '',
  certifications: practitioner.Certifications || '',
  short_bio: practitioner['Short Bio'] || '',
  detailed_bio: practitioner['Detailed Bio'] || '',
  bio: practitioner.Bio || '',
  practices: joinValues(practitioner.practices),
  specializes_in: practitioner['Specializes In'] || '',
  gender_focus: joinValues(practitioner['Gender Focus']),
  service_categories: joinValues(practitioner['Service categories']),
  photo_url: practitioner.Photo || '',
  consultation_url: practitioner.Consultation || '',
  booking_url: practitioner['Book Me'] || '',
  published: practitioner.published !== false,
});

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [practitioners, setPractitioners] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [search, setSearch] = useState('');
  const [videoReviews, setVideoReviews] = useState([]);
  const [videoReviewsLoading, setVideoReviewsLoading] = useState(false);
  const [videoReviewForm, setVideoReviewForm] = useState(emptyVideoReviewForm);
  const [editingVideoReviewId, setEditingVideoReviewId] = useState(null);
  const [videoReviewSaving, setVideoReviewSaving] = useState(false);
  const [writtenReviews, setWrittenReviews] = useState([]);
  const [writtenReviewsLoading, setWrittenReviewsLoading] = useState(false);
  const [writtenReviewForm, setWrittenReviewForm] = useState(emptyWrittenReviewForm);
  const [writtenReviewSaving, setWrittenReviewSaving] = useState(false);
  const [writtenReviewStatusFilter, setWrittenReviewStatusFilter] = useState('pending');
  const [moderationReviews, setModerationReviews] = useState([]);
  const [moderationLoading, setModerationLoading] = useState(false);
  const [moderationError, setModerationError] = useState('');
  const [moderationFilter, setModerationFilter] = useState('pending');
  const [moderationUpdatingId, setModerationUpdatingId] = useState(null);

  const loadPractitioners = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/practitioners`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Unable to load practitioner profiles');
      setPractitioners(await response.json());
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const loadModerationReviews = async () => {
    setModerationLoading(true);
    setModerationError('');
    try {
      const response = await fetch(`${API_BASE}/api/admin/written-reviews`, {
        credentials: 'include',
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'Unable to load written reviews');
      setModerationReviews(result);
    } catch (error) {
      setModerationError(error.message);
    } finally {
      setModerationLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      loadPractitioners();
      loadModerationReviews();
    }
  }, [user]);

  const visiblePractitioners = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return practitioners;
    return practitioners.filter((item) =>
      [item.Name, item.Title, item.Archetype, item['Service categories']]
        .flat()
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [practitioners, search]);

  const visibleWrittenReviews = writtenReviews.filter(
    (review) => writtenReviewStatusFilter === 'all' || review.review_status === writtenReviewStatusFilter
  );
  const pendingWrittenReviewCount = writtenReviews.filter(
    (review) => review.review_status === 'pending'
  ).length;
  const pendingModerationReviewCount = moderationReviews.filter(
    (review) => review.review_status === 'pending'
  ).length;
  const visibleModerationReviews = moderationReviews.filter(
    (review) => moderationFilter === 'all' || review.review_status === moderationFilter
  );

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateVideoReviewField = (field, value) =>
    setVideoReviewForm((current) => ({ ...current, [field]: value }));
  const updateWrittenReviewField = (field, value) =>
    setWrittenReviewForm((current) => ({ ...current, [field]: value }));

  const updateModerationReviewStatus = async (review, reviewStatus) => {
    setModerationUpdatingId(review.id);
    setModerationError('');
    try {
      const response = await fetch(`${API_BASE}/api/admin/written-reviews/${review.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ review_status: reviewStatus }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'Could not update written review');
      setModerationReviews((current) =>
        current.map((item) =>
          item.id === review.id ? { ...item, review_status: reviewStatus } : item
        )
      );
      setMessage({
        type: 'success',
        text: reviewStatus === 'approved'
          ? 'Written review approved.'
          : reviewStatus === 'rejected'
            ? 'Written review rejected.'
            : 'Written review returned to pending.',
      });
    } catch (error) {
      setModerationError(error.message);
    } finally {
      setModerationUpdatingId(null);
    }
  };

  const loadVideoReviews = async (practitionerId) => {
    setVideoReviewsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/practitioners/${practitionerId}/video-reviews`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error('Unable to load video reviews');
      setVideoReviews(await response.json());
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
      setVideoReviews([]);
    } finally {
      setVideoReviewsLoading(false);
    }
  };

  const loadWrittenReviews = async (practitionerId) => {
    setWrittenReviewsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/practitioners/${practitionerId}/reviews`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error('Unable to load written reviews');
      setWrittenReviews(await response.json());
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
      setWrittenReviews([]);
    } finally {
      setWrittenReviewsLoading(false);
    }
  };

  const startNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setVideoReviews([]);
    setVideoReviewForm(emptyVideoReviewForm);
    setEditingVideoReviewId(null);
    setWrittenReviews([]);
    setWrittenReviewForm(emptyWrittenReviewForm);
    setWrittenReviewStatusFilter('pending');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startEdit = (practitioner) => {
    const practitionerId = practitioner.id || practitioner.ID;
    setEditingId(practitionerId);
    setForm(toForm(practitioner));
    setVideoReviewForm(emptyVideoReviewForm);
    setEditingVideoReviewId(null);
    setWrittenReviewForm(emptyWrittenReviewForm);
    setWrittenReviewStatusFilter('pending');
    loadVideoReviews(practitionerId);
    loadWrittenReviews(practitionerId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    const payload = {
      ...form,
      practices: splitValues(form.practices),
      gender_focus: splitValues(form.gender_focus),
      service_categories: splitValues(form.service_categories),
    };
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/practitioners${editingId ? `/${editingId}` : ''}`,
        {
          method: editingId ? 'PUT' : 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'Could not save profile');
      setMessage({ type: 'success', text: editingId ? 'Profile updated.' : 'Profile created.' });
      setEditingId(null);
      setForm(emptyForm);
      setVideoReviews([]);
      setVideoReviewForm(emptyVideoReviewForm);
      setEditingVideoReviewId(null);
      setWrittenReviews([]);
      setWrittenReviewForm(emptyWrittenReviewForm);
      await loadPractitioners();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleVideoReviewSubmit = async (event) => {
    event.preventDefault();
    if (!editingId) return;

    setVideoReviewSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const endpoint = `${API_BASE}/api/admin/practitioners/${editingId}/video-reviews${
        editingVideoReviewId ? `/${editingVideoReviewId}` : ''
      }`;
      const response = await fetch(endpoint, {
        method: editingVideoReviewId ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...videoReviewForm,
          sort_order: Number(videoReviewForm.sort_order) || 0,
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'Could not save video review');
      setMessage({
        type: 'success',
        text: editingVideoReviewId ? 'Video review updated.' : 'Video review added.',
      });
      setVideoReviewForm(emptyVideoReviewForm);
      setEditingVideoReviewId(null);
      await loadVideoReviews(editingId);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setVideoReviewSaving(false);
    }
  };

  const handleWrittenReviewSubmit = async (event) => {
    event.preventDefault();
    if (!editingId) return;

    setWrittenReviewSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/practitioners/${editingId}/reviews`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...writtenReviewForm,
            rating: Number(writtenReviewForm.rating),
            review_status: 'approved',
            review_date: writtenReviewForm.review_date || null,
          }),
        }
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'Could not add written review');
      setMessage({ type: 'success', text: 'Published written review added.' });
      setWrittenReviewForm(emptyWrittenReviewForm);
      setWrittenReviewStatusFilter('all');
      await loadWrittenReviews(editingId);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setWrittenReviewSaving(false);
    }
  };

  const updateWrittenReviewStatus = async (review, reviewStatus) => {
    if (!editingId) return;
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/practitioners/${editingId}/reviews/${review.id}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ review_status: reviewStatus }),
        }
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'Could not update written review');
      setMessage({
        type: 'success',
        text: reviewStatus === 'approved' ? 'Written review approved.' : 'Written review rejected.',
      });
      await loadWrittenReviews(editingId);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const deleteWrittenReview = async (review) => {
    if (
      !editingId ||
      !window.confirm(`Delete the written review from ${review.reviewer_name || 'this client'}?`)
    ) {
      return;
    }
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/practitioners/${editingId}/reviews/${review.id}`,
        { method: 'DELETE', credentials: 'include' }
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'Could not delete written review');
      setMessage({ type: 'success', text: 'Written review deleted.' });
      await loadWrittenReviews(editingId);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const startEditVideoReview = (review) => {
    setEditingVideoReviewId(review.id);
    setVideoReviewForm({
      reviewer_name: review.reviewer_name || '',
      video_url: review.video_url || '',
      caption: review.caption || '',
      poster_url: review.poster_url || '',
      sort_order: review.sort_order || 0,
      published: review.published !== false,
    });
  };

  const handleDeleteVideoReview = async (review) => {
    if (!editingId || !window.confirm(`Delete the video review from ${review.reviewer_name || 'this client'}?`)) {
      return;
    }
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/practitioners/${editingId}/video-reviews/${review.id}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.detail || 'Could not delete video review');
      }
      setMessage({ type: 'success', text: 'Video review deleted.' });
      if (editingVideoReviewId === review.id) {
        setEditingVideoReviewId(null);
        setVideoReviewForm(emptyVideoReviewForm);
      }
      await loadVideoReviews(editingId);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const handleDelete = async (practitioner) => {
    const id = practitioner.id || practitioner.ID;
    if (!window.confirm(`Delete ${practitioner.Name}? This cannot be undone.`)) return;
    try {
      const response = await fetch(`${API_BASE}/api/admin/practitioners/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.detail || 'Could not delete profile');
      }
      setMessage({ type: 'success', text: 'Profile deleted.' });
      if (editingId === id) startNew();
      await loadPractitioners();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-white pt-24 flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <Shield className="mx-auto mb-5 h-12 w-12 text-purple-600" />
          <h1 className="text-3xl font-bold text-purple-800 mb-3 font-title">Admin access required</h1>
          <p className="text-gray-600">Sign in with an administrator account to manage practitioner profiles.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap gap-4 items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-purple-500 font-semibold">Admin workspace</p>
            <h1 className="text-3xl font-bold text-purple-900 font-title">Practitioner profiles</h1>
            <p className="text-gray-600 mt-1">Edit the public directory without changing code.</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-4 py-2 text-sm font-medium text-purple-700">
              <Shield className="h-4 w-4" /> Administrator
            </span>
            <Link
              to="/admin/users"
              className="inline-flex items-center gap-2 rounded-md border border-purple-200 bg-white px-4 py-2 text-sm font-semibold text-purple-800 transition hover:bg-purple-50"
            >
              <Users className="h-4 w-4" /> Users
            </Link>
            <Link
              to="/admin/analytics"
              className="inline-flex items-center gap-2 rounded-md border border-purple-200 bg-white px-4 py-2 text-sm font-semibold text-purple-800 transition hover:bg-purple-50"
            >
              <BarChart3 className="h-4 w-4" /> Website stats
            </Link>
            <Link
              to="/admin/landing-pages"
              className="inline-flex items-center rounded-md bg-purple-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-purple-800"
            >
              Landing page generator
            </Link>
            <Button onClick={logout} variant="outline">Log out</Button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message.text && (
          <div className={`mb-6 rounded-lg border px-4 py-3 ${message.type === 'error'
            ? 'border-red-200 bg-red-50 text-red-700'
            : 'border-green-200 bg-green-50 text-green-700'}`}>
            {message.text}
          </div>
        )}

        <Card className="mb-8">
          <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Written review approvals</CardTitle>
              <p className="mt-1 text-sm text-gray-500">
                Approve or reject visitor submissions from one queue, without opening a practitioner profile.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-800">
                {pendingModerationReviewCount} pending
              </span>
              <label className="sr-only" htmlFor="moderation-review-filter">Filter reviews</label>
              <select
                id="moderation-review-filter"
                className="h-9 rounded-md border border-gray-300 bg-white px-3 text-sm"
                value={moderationFilter}
                onChange={(event) => setModerationFilter(event.target.value)}
              >
                <option value="pending">Pending ({pendingModerationReviewCount})</option>
                <option value="approved">
                  Approved ({moderationReviews.filter((review) => review.review_status === 'approved').length})
                </option>
                <option value="rejected">
                  Rejected ({moderationReviews.filter((review) => review.review_status === 'rejected').length})
                </option>
                <option value="all">All reviews ({moderationReviews.length})</option>
              </select>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {moderationError && (
              <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {moderationError}
              </p>
            )}
            {moderationLoading ? (
              <p className="text-sm text-gray-500">Loading reviews for approval...</p>
            ) : visibleModerationReviews.length === 0 ? (
              <p className="rounded-xl border border-dashed p-5 text-sm text-gray-500">
                {moderationFilter === 'pending'
                  ? 'No written reviews are awaiting approval.'
                  : 'No written reviews match this filter.'}
              </p>
            ) : (
              visibleModerationReviews.map((review) => (
                <article key={review.id} className="rounded-xl border bg-white p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 className="font-semibold text-gray-900">
                        {review.reviewer_name || 'Anonymous Client'}
                      </h2>
                      <p className="mt-1 text-sm text-gray-500">
                        {review.practitioner_name || 'Company-wide'} · {review.rating}/5 stars
                      </p>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${
                      review.review_status === 'approved'
                        ? 'bg-green-100 text-green-700'
                        : review.review_status === 'rejected'
                          ? 'bg-gray-100 text-gray-600'
                          : 'bg-amber-100 text-amber-800'
                    }`}>
                      {review.review_status}
                    </span>
                  </div>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                    {review.comment}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {review.review_status === 'pending' ? (
                      <>
                        <Button
                          size="sm"
                          disabled={moderationUpdatingId === review.id}
                          className="bg-purple-700 hover:bg-purple-800"
                          onClick={() => updateModerationReviewStatus(review, 'approved')}
                        >
                          {moderationUpdatingId === review.id ? 'Saving...' : 'Approve'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={moderationUpdatingId === review.id}
                          onClick={() => updateModerationReviewStatus(review, 'rejected')}
                        >
                          Reject
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={moderationUpdatingId === review.id}
                        onClick={() => updateModerationReviewStatus(review, 'pending')}
                      >
                        Return to pending
                      </Button>
                    )}
                  </div>
                </article>
              ))
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-8 items-start">
          <div className="space-y-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-4">
                <CardTitle>{editingId ? 'Edit profile' : 'Add a practitioner'}</CardTitle>
                {editingId && (
                  <Button variant="ghost" onClick={startNew}>
                    <X className="h-4 w-4 mr-2" /> Cancel
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    ['name', 'Name', true],
                    ['title', 'Title', false],
                    ['archetype', 'Archetype', false],
                    ['slug', 'URL slug', false],
                    ['certifications', 'Certifications', false],
                    ['specializes_in', 'Specializes in', false],
                  ].map(([field, label, required]) => (
                    <label key={field} className="block">
                      <span className="text-sm font-medium text-gray-700">{label}</span>
                      <Input
                        className="mt-1"
                        value={form[field]}
                        required={required}
                        onChange={(event) => updateField(field, event.target.value)}
                      />
                    </label>
                  ))}
                </div>

                {[
                  ['short_bio', 'Short bio'],
                  ['detailed_bio', 'Detailed bio'],
                  ['bio', 'Additional bio'],
                ].map(([field, label]) => (
                  <label key={field} className="block">
                    <span className="text-sm font-medium text-gray-700">{label}</span>
                    <Textarea
                      className="mt-1"
                      rows={field === 'short_bio' ? 4 : 6}
                      value={form[field]}
                      onChange={(event) => updateField(field, event.target.value)}
                    />
                  </label>
                ))}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    ['practices', 'Practices (comma separated)'],
                    ['gender_focus', 'Gender focus (comma separated)'],
                    ['service_categories', 'Service categories (comma separated)'],
                  ].map(([field, label]) => (
                    <label key={field} className="block">
                      <span className="text-sm font-medium text-gray-700">{label}</span>
                      <Textarea
                        className="mt-1"
                        rows={3}
                        value={form[field]}
                        onChange={(event) => updateField(field, event.target.value)}
                      />
                    </label>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    ['photo_url', 'Photo URL'],
                    ['consultation_url', 'Consultation link'],
                    ['booking_url', 'Booking link'],
                  ].map(([field, label]) => (
                    <label key={field} className="block">
                      <span className="text-sm font-medium text-gray-700">{label}</span>
                      <Input
                        className="mt-1"
                        type="url"
                        placeholder="https://..."
                        value={form[field]}
                        onChange={(event) => updateField(field, event.target.value)}
                      />
                    </label>
                  ))}
                </div>

                <label className="flex items-center gap-3 rounded-lg border bg-gray-50 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={form.published}
                    onChange={(event) => updateField('published', event.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-purple-600"
                  />
                  <span>
                    <span className="block font-medium text-gray-800">Published in directory</span>
                    <span className="block text-sm text-gray-500">Uncheck to save this profile privately.</span>
                  </span>
                </label>

                <Button type="submit" disabled={saving} className="bg-purple-700 hover:bg-purple-800">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : editingId ? 'Save changes' : 'Create profile'}
                </Button>
                </form>
              </CardContent>
            </Card>

            {editingId && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between gap-3">
                    <CardTitle className="flex items-center gap-2">
                      <Video className="h-5 w-5 text-purple-600" />
                      Video reviews
                    </CardTitle>
                    <span className="rounded-full bg-purple-50 px-3 py-1 text-sm font-medium text-purple-700">
                      {videoReviews.length}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">
                    Add direct video files or hosted links such as Bunny Stream, YouTube, and Vimeo. Published reviews appear before text reviews.
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <form onSubmit={handleVideoReviewSubmit} className="space-y-4 rounded-xl border bg-gray-50 p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="block">
                        <span className="text-sm font-medium text-gray-700">Reviewer name</span>
                        <Input
                          className="mt-1"
                          placeholder="Anonymous Client"
                          value={videoReviewForm.reviewer_name}
                          onChange={(event) => updateVideoReviewField('reviewer_name', event.target.value)}
                        />
                      </label>
                      <label className="block">
                        <span className="text-sm font-medium text-gray-700">Display order</span>
                        <Input
                          className="mt-1"
                          type="number"
                          min="0"
                          value={videoReviewForm.sort_order}
                          onChange={(event) => updateVideoReviewField('sort_order', event.target.value)}
                        />
                      </label>
                    </div>

                    <label className="block">
                      <span className="text-sm font-medium text-gray-700">Video URL</span>
                      <Input
                        className="mt-1"
                        type="url"
                        placeholder="Bunny Stream, video file, YouTube, Vimeo, Google Drive, or hosted video link"
                        required
                        value={videoReviewForm.video_url}
                        onChange={(event) => updateVideoReviewField('video_url', event.target.value)}
                      />
                      <span className="mt-1 block text-xs text-gray-500">
                        Bunny Stream play links are converted to the embedded player automatically.
                      </span>
                    </label>

                    <label className="block">
                      <span className="text-sm font-medium text-gray-700">Custom thumbnail URL (optional)</span>
                      <Input
                        className="mt-1"
                        type="url"
                        placeholder="Leave blank to use the video’s own preview frame"
                        value={videoReviewForm.poster_url}
                        onChange={(event) => updateVideoReviewField('poster_url', event.target.value)}
                      />
                    </label>

                    <label className="block">
                      <span className="text-sm font-medium text-gray-700">Caption (optional)</span>
                      <Textarea
                        className="mt-1"
                        rows={3}
                        placeholder="What would you like visitors to know about this review?"
                        value={videoReviewForm.caption}
                        onChange={(event) => updateVideoReviewField('caption', event.target.value)}
                      />
                    </label>

                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={videoReviewForm.published}
                        onChange={(event) => updateVideoReviewField('published', event.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-purple-600"
                      />
                      <span className="text-sm font-medium text-gray-700">Show this video review publicly</span>
                    </label>

                    <div className="flex flex-wrap gap-3">
                      <Button type="submit" disabled={videoReviewSaving} className="bg-purple-700 hover:bg-purple-800">
                        <Save className="h-4 w-4 mr-2" />
                        {videoReviewSaving
                          ? 'Saving...'
                          : editingVideoReviewId
                            ? 'Save video review'
                            : 'Add video review'}
                      </Button>
                      {editingVideoReviewId && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setEditingVideoReviewId(null);
                            setVideoReviewForm(emptyVideoReviewForm);
                          }}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </form>

                  {videoReviewsLoading ? (
                    <p className="text-sm text-gray-500">Loading video reviews...</p>
                  ) : videoReviews.length === 0 ? (
                    <div className="rounded-xl border border-dashed p-5 text-sm text-gray-500">
                      No video reviews have been added for this practitioner yet.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {videoReviews.map((review) => (
                        <div key={review.id} className="flex flex-wrap items-center gap-3 rounded-xl border bg-white p-3">
                          <Video className="h-5 w-5 shrink-0 text-purple-500" />
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900 truncate">
                              {review.reviewer_name || 'Anonymous Client'}
                            </p>
                            <p className="text-sm text-gray-500 truncate">{review.video_url}</p>
                          </div>
                          <span className={`text-xs font-medium ${review.published ? 'text-green-600' : 'text-gray-400'}`}>
                            {review.published ? 'Published' : 'Hidden'}
                          </span>
                          <Button size="sm" variant="outline" onClick={() => startEditVideoReview(review)}>
                            <Edit className="h-3.5 w-3.5 mr-1" /> Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600"
                            onClick={() => handleDeleteVideoReview(review)}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {editingId && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between gap-3">
                    <CardTitle>Written reviews</CardTitle>
                    <span className="rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-800">
                      {pendingWrittenReviewCount} pending
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">
                    Reviews submitted from this profile appear here for approval. Reviews added by an admin are published immediately.
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <form
                    onSubmit={handleWrittenReviewSubmit}
                    className="space-y-4 rounded-xl border bg-gray-50 p-4"
                  >
                    <h3 className="font-semibold text-gray-800">Add a written review</h3>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <label className="block">
                        <span className="text-sm font-medium text-gray-700">Reviewer name</span>
                        <Input
                          className="mt-1"
                          placeholder="Anonymous Client"
                          maxLength={120}
                          value={writtenReviewForm.reviewer_name}
                          onChange={(event) => updateWrittenReviewField('reviewer_name', event.target.value)}
                        />
                      </label>
                      <label className="block">
                        <span className="text-sm font-medium text-gray-700">Rating</span>
                        <select
                          className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                          value={writtenReviewForm.rating}
                          onChange={(event) => updateWrittenReviewField('rating', Number(event.target.value))}
                        >
                          {[5, 4, 3, 2, 1].map((rating) => (
                            <option key={rating} value={rating}>{rating} {rating === 1 ? 'star' : 'stars'}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <label className="block">
                      <span className="text-sm font-medium text-gray-700">Review</span>
                      <Textarea
                        className="mt-1"
                        rows={4}
                        minLength={10}
                        maxLength={4000}
                        required
                        value={writtenReviewForm.comment}
                        onChange={(event) => updateWrittenReviewField('comment', event.target.value)}
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm font-medium text-gray-700">Review date (optional)</span>
                      <Input
                        className="mt-1"
                        type="date"
                        value={writtenReviewForm.review_date}
                        onChange={(event) => updateWrittenReviewField('review_date', event.target.value)}
                      />
                    </label>
                    <Button
                      type="submit"
                      disabled={writtenReviewSaving}
                      className="bg-purple-700 hover:bg-purple-800"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      {writtenReviewSaving ? 'Adding...' : 'Add published review'}
                    </Button>
                  </form>

                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      Review status
                      <select
                        className="h-9 rounded-md border border-gray-300 bg-white px-3"
                        value={writtenReviewStatusFilter}
                        onChange={(event) => setWrittenReviewStatusFilter(event.target.value)}
                      >
                        <option value="pending">Pending ({pendingWrittenReviewCount})</option>
                        <option value="approved">
                          Approved ({writtenReviews.filter((review) => review.review_status === 'approved').length})
                        </option>
                        <option value="rejected">
                          Rejected ({writtenReviews.filter((review) => review.review_status === 'rejected').length})
                        </option>
                        <option value="all">All reviews ({writtenReviews.length})</option>
                      </select>
                    </label>
                    {writtenReviewsLoading && <span className="text-sm text-gray-500">Loading reviews...</span>}
                  </div>

                  {writtenReviewsLoading ? null : visibleWrittenReviews.length === 0 ? (
                    <div className="rounded-xl border border-dashed p-5 text-sm text-gray-500">
                      {writtenReviewStatusFilter === 'pending'
                        ? 'No reviews are waiting for approval.'
                        : 'No reviews in this status.'}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {visibleWrittenReviews.map((review) => (
                        <article key={review.id} className="space-y-3 rounded-xl border bg-white p-4">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-900">
                                {review.reviewer_name || 'Anonymous Client'}
                                <span className="ml-2 text-sm font-normal text-gray-500">
                                  · {review.rating}/5
                                </span>
                              </p>
                              <details className="mt-2">
                                <summary className="cursor-pointer text-sm font-medium text-purple-700">
                                  Read review
                                </summary>
                                <p className="mt-2 whitespace-pre-line break-words text-sm leading-6 text-gray-700">
                                  {review.comment}
                                </p>
                              </details>
                            </div>
                            <span
                              className={`rounded-full px-3 py-1 text-xs font-medium ${
                                review.review_status === 'approved'
                                  ? 'bg-green-100 text-green-800'
                                  : review.review_status === 'pending'
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {review.review_status === 'approved'
                                ? 'Approved'
                                : review.review_status === 'pending'
                                  ? 'Pending'
                                  : 'Rejected'}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {review.review_status !== 'approved' && (
                              <Button
                                size="sm"
                                onClick={() => updateWrittenReviewStatus(review, 'approved')}
                                className="bg-green-700 hover:bg-green-800"
                              >
                                Approve
                              </Button>
                            )}
                            {review.review_status !== 'rejected' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateWrittenReviewStatus(review, 'rejected')}
                              >
                                Reject
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600"
                              onClick={() => deleteWrittenReview(review)}
                            >
                              <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                            </Button>
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <CardTitle>Directory records</CardTitle>
                <Button size="sm" onClick={startNew}><Plus className="h-4 w-4 mr-1" /> New</Button>
              </div>
              <Input
                className="mt-4"
                placeholder="Search names, titles, or services..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </CardHeader>
            <CardContent className="space-y-3">
              {loading ? (
                <p className="text-gray-500">Loading profiles...</p>
              ) : visiblePractitioners.length === 0 ? (
                <p className="text-gray-500">No profiles found.</p>
              ) : visiblePractitioners.map((practitioner) => {
                const id = practitioner.id || practitioner.ID;
                return (
                  <div key={id} className="flex gap-3 rounded-xl border bg-white p-3">
                    <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-purple-100 flex items-center justify-center">
                      {practitioner.Photo ? (
                        <img src={practitioner.Photo} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" />
                      ) : (
                        <Image className="h-5 w-5 text-purple-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-semibold text-gray-900 truncate">{practitioner.Name}</h3>
                          <p className="text-sm text-gray-500 truncate">{practitioner.Title}</p>
                        </div>
                        {practitioner.published ? (
                          <Eye className="h-4 w-4 shrink-0 text-green-600" aria-label="Published" />
                        ) : (
                          <EyeOff className="h-4 w-4 shrink-0 text-gray-400" aria-label="Hidden" />
                        )}
                      </div>
                      <div className="mt-2 flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => startEdit(practitioner)}>
                          <Edit className="h-3.5 w-3.5 mr-1" /> Edit
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(practitioner)}>
                          <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;